import { motion } from "framer-motion";
import { SEO } from "@/components/shared";
import { fadeInUp, staggerContainer } from "@/constants";

export default function PrivacyPage() {
  return (
    <div className="pt-24">
      <SEO
        title="Privacy Policy"
        description="Privacy Policy of Qbic Catalyst Partners explaining how we collect, use, and protect your personal information."
        keywords="privacy policy, data protection, Qbic Catalyst, personal information, GDPR, data security"
        path="/privacy"
      />
      <section className="section bg-[#0A1628]">
        <div className="container-custom">
          <motion.div initial="initial" animate="animate" variants={staggerContainer}>
            <motion.p variants={fadeInUp} className="text-[#C9A962] text-sm uppercase tracking-[0.2em] mb-4">Legal</motion.p>
            <motion.h1 variants={fadeInUp} className="text-4xl md:text-5xl font-bold mb-6">Privacy Policy</motion.h1>
            <motion.p variants={fadeInUp} className="text-[#A0ABC0] mb-2">Effective Date: 01 January 2025</motion.p>
            <motion.p variants={fadeInUp} className="text-[#A0ABC0] mb-12">Last Updated: February 2026</motion.p>
          </motion.div>
        </div>
      </section>

      <section className="section bg-[#1A2A40]">
        <div className="container-custom max-w-4xl">
          <div className="prose-custom space-y-10">

            <div>
              <h2>1. Introduction</h2>
              <p>Qbic Catalyst Partners Pvt. Ltd. ("QCP", "we", "us", or "our") is committed to protecting and respecting your privacy. This Privacy Policy explains how we collect, use, store, and safeguard your personal information when you visit our website at www.qbic-catalyst.com (the "Site"), use our services, or interact with us.</p>
              <p>As an ISO/IEC 27001:2022 certified organization, we adhere to the highest standards of information security management in all our data processing activities.</p>
            </div>

            <div>
              <h2>2. Information We Collect</h2>
              <p>We may collect the following types of information:</p>
              <h3>a) Information You Provide</h3>
              <ul>
                <li><strong>Contact Form Submissions:</strong> Name, email address, phone number, company name, service of interest, and message content when you submit our contact form.</li>
                <li><strong>Program Enrollment:</strong> Name, educational details, institutional affiliation, and other information required for program registration.</li>
                <li><strong>Event Registration:</strong> Name, email, designation, and institutional details when registering for webinars or events.</li>
                <li><strong>Newsletter Subscription:</strong> Email address when you subscribe to our communications.</li>
              </ul>
              <h3>b) Information Collected Automatically</h3>
              <ul>
                <li><strong>Usage Data:</strong> Pages visited, time spent on pages, navigation paths, and referring URLs.</li>
                <li><strong>Device Information:</strong> Browser type, operating system, screen resolution, and device type.</li>
                <li><strong>IP Address:</strong> Your Internet Protocol address, used for security, analytics, and spam prevention.</li>
                <li><strong>Cookies:</strong> Small data files stored on your device to enhance your browsing experience (see Section 7).</li>
              </ul>
            </div>

            <div>
              <h2>3. How We Use Your Information</h2>
              <p>We use the information collected for the following purposes:</p>
              <ul>
                <li><strong>Service Delivery:</strong> To respond to your inquiries, process program enrollments, and deliver consulting and training services.</li>
                <li><strong>Communication:</strong> To send you relevant updates about our services, programs, events, and industry insights (with your consent where required).</li>
                <li><strong>Website Improvement:</strong> To analyze usage patterns and improve the Site's functionality, content, and user experience.</li>
                <li><strong>Security:</strong> To protect the Site against spam, fraud, unauthorized access, and other security threats.</li>
                <li><strong>Legal Compliance:</strong> To comply with applicable laws, regulations, and legal processes.</li>
                <li><strong>Certification & Records:</strong> To issue certifications, maintain program records, and facilitate placement assistance for program participants.</li>
              </ul>
            </div>

            <div>
              <h2>4. Legal Basis for Processing</h2>
              <p>We process your personal information based on the following legal grounds:</p>
              <ul>
                <li><strong>Consent:</strong> When you voluntarily submit information through our contact form, register for events, or subscribe to communications.</li>
                <li><strong>Contractual Necessity:</strong> When processing is necessary to fulfill our obligations under a service agreement or program enrollment.</li>
                <li><strong>Legitimate Interest:</strong> For website analytics, security measures, and improving our services, where such interests are not overridden by your data protection rights.</li>
                <li><strong>Legal Obligation:</strong> Where required to comply with applicable laws and regulations.</li>
              </ul>
            </div>

            <div>
              <h2>5. Data Sharing & Disclosure</h2>
              <p>We do not sell, rent, or trade your personal information to third parties. We may share your information in the following limited circumstances:</p>
              <ul>
                <li><strong>Service Providers:</strong> With trusted third-party service providers who assist us in operating the Site, delivering services, or processing data (e.g., email service providers, hosting services). These providers are contractually bound to protect your data.</li>
                <li><strong>Program Partners:</strong> With institutional or industry partners (e.g., Intel, universities) solely to the extent necessary for program delivery, certification, or placement facilitation, and only with your knowledge.</li>
                <li><strong>Legal Requirements:</strong> When required by law, regulation, legal process, or government request.</li>
                <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of the business assets.</li>
              </ul>
            </div>

            <div>
              <h2>6. Data Retention</h2>
              <p>We retain your personal information only for as long as necessary to fulfill the purposes for which it was collected:</p>
              <ul>
                <li><strong>Contact Inquiries:</strong> Retained for up to 2 years after the last interaction.</li>
                <li><strong>Program Records:</strong> Certification and enrollment records are retained indefinitely as part of our academic records.</li>
                <li><strong>Marketing Communications:</strong> Until you unsubscribe or withdraw consent.</li>
                <li><strong>Website Analytics:</strong> Aggregated, anonymized data may be retained indefinitely for trend analysis.</li>
              </ul>
            </div>

            <div>
              <h2>7. Cookies & Tracking</h2>
              <p>Our Site may use cookies and similar technologies to:</p>
              <ul>
                <li>Remember your preferences and settings.</li>
                <li>Analyze Site traffic and usage patterns.</li>
                <li>Ensure the security of the Site.</li>
              </ul>
              <p>You can control cookie settings through your browser preferences. Disabling cookies may affect certain features of the Site.</p>
            </div>

            <div>
              <h2>8. Data Security</h2>
              <p>As an ISO/IEC 27001:2022 certified organization, we implement robust technical and organizational measures to protect your personal information, including:</p>
              <ul>
                <li>Encryption of data in transit (TLS/SSL) and at rest.</li>
                <li>Access controls and authentication mechanisms.</li>
                <li>Regular security assessments and audits.</li>
                <li>Employee training on data protection and security practices.</li>
                <li>Incident response procedures for data breach management.</li>
              </ul>
              <p>While we strive to protect your personal information, no method of electronic transmission or storage is 100% secure. We cannot guarantee absolute security but are committed to maintaining the highest industry standards.</p>
            </div>

            <div>
              <h2>9. Your Rights</h2>
              <p>Depending on your jurisdiction, you may have the following rights regarding your personal information:</p>
              <ul>
                <li><strong>Access:</strong> Request a copy of the personal information we hold about you.</li>
                <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information.</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information, subject to legal and contractual obligations.</li>
                <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications at any time by clicking the unsubscribe link in our emails or contacting us directly.</li>
                <li><strong>Data Portability:</strong> Request a copy of your data in a structured, commonly used format.</li>
                <li><strong>Objection:</strong> Object to the processing of your personal information where we rely on legitimate interests.</li>
              </ul>
              <p>To exercise any of these rights, please contact us at <a href="mailto:info@qbiccatalyst.com" className="text-[#C9A962] hover:underline">info@qbiccatalyst.com</a>. We will respond to your request within 30 days.</p>
            </div>

            <div>
              <h2>10. Children's Privacy</h2>
              <p>Our Site and services are not directed at individuals under the age of 16. We do not knowingly collect personal information from children. If you believe we have inadvertently collected information from a child, please contact us immediately and we will take steps to delete such information.</p>
            </div>

            <div>
              <h2>11. International Data Transfers</h2>
              <p>Your information may be processed and stored on servers located outside your country of residence. Where such transfers occur, we ensure appropriate safeguards are in place to protect your data in accordance with applicable data protection laws.</p>
            </div>

            <div>
              <h2>12. Changes to This Policy</h2>
              <p>We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated "Last Updated" date. We encourage you to review this page periodically. For material changes, we will endeavour to provide notice through the Site or via email.</p>
            </div>

            <div>
              <h2>13. Contact Us</h2>
              <p>If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:</p>
              <p><strong>Qbic Catalyst Partners Pvt. Ltd.</strong><br />
              Email: <a href="mailto:info@qbiccatalyst.com" className="text-[#C9A962] hover:underline">info@qbiccatalyst.com</a><br />
              Website: <a href="https://www.qbic-catalyst.com" className="text-[#C9A962] hover:underline">www.qbic-catalyst.com</a></p>
              <p className="text-sm text-[#A0ABC0] mt-6 italic">Qbic Catalyst Partners Pvt. Ltd. is an ISO/IEC 27001:2022 certified and Govt. of India recognized Ed-Tech company.</p>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}
